<template>
    <div class="container-fluid-grid">
        <div class="row">
            <div class="col-12">
                <p class="assistant-intro">
                    O assistente de IA (Claude Desktop) pode, <b>só com a sua autorização</b>, saber qual
                    tela você está acessando e abrir telas para você. As permissões abaixo ficam salvas
                    apenas neste navegador e podem ser desligadas a qualquer momento.
                </p>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <label>Permitir que o assistente veja qual tela estou acessando?</label>
                <div class="checkbox-inline" style="padding: 5px">
                    <label>
                        <input type="checkbox"
                               v-model="sharesScreen"
                               @change="onShareChange"
                               class="checkbox green m"/><b :class="{'c-theme': sharesScreen, 'c-red': !sharesScreen}"> {{ sharesScreen ? 'Sim' : 'Não' }}</b></label>
                </div>
                <small>
                    Envia ao assistente apenas o nome da tela e o registro aberto (ex.: "proposta 4521")
                    enquanto você navega. Expira sozinho após 2 minutos parado. É o que responde ao
                    assistente quando você pergunta "qual tela estou vendo?".
                </small>
            </div>
        </div>

        <div class="row" style="margin-top: 15px">
            <div class="col-12">
                <label>Permitir que o assistente abra telas para mim?</label>
                <div class="checkbox-inline" style="padding: 5px">
                    <label>
                        <input type="checkbox"
                               v-model="canNavigate"
                               @change="onNavigateChange"
                               class="checkbox green m"/><b :class="{'c-theme': canNavigate, 'c-red': !canNavigate}"> {{ canNavigate ? 'Sim' : 'Não' }}</b></label>
                </div>
                <small>
                    Deixa o assistente abrir uma tela ou registro no seu navegador. Com esta opção
                    desligada, ele apenas sugere um link para você clicar. O assistente <b>nunca</b>
                    salva nem emite nada por você — apenas navega.
                </small>
            </div>
        </div>

        <!-- Chaves de API: recurso de auto-servico ligado ao assistente. Vive aqui, no
             perfil, e nao numa tela dedicada — a chave herda exatamente as permissoes
             do dono, entao todo perfil gerencia as suas. -->
        <hr class="section-divider"/>

        <div class="row">
            <div class="col-12">
                <h5 class="section-title">Chaves de API</h5>
                <p class="intro">
                    Chaves de API permitem que assistentes de IA (Claude Code, Cursor) acessem o Agrisoy
                    em seu nome. Cada chave usa <strong>exatamente as suas permissões</strong> — ela não
                    acessa nada além do que você mesmo acessa.
                </p>
            </div>
        </div>

        <!-- Criar -->
        <form @submit.prevent.stop="create">
            <div class="form-row">
                <div class="col-md-5">
                    <div class="form-group">
                        <label for="apiKeyName">Nome da chave</label>
                        <input id="apiKeyName" type="text" class="form-control form-control-sm"
                               maxlength="100" placeholder="Ex.: Meu notebook"
                               v-model="form.name"/>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="form-group">
                        <label for="apiKeyExpires">Expira em</label>
                        <select id="apiKeyExpires" class="form-control form-control-sm"
                                v-model="form.expires_in_days">
                            <option value="30">30 dias</option>
                            <option value="90">90 dias</option>
                            <option value="180">180 dias</option>
                            <option value="365">1 ano</option>
                        </select>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="form-group">
                        <br/>
                        <button type="submit" class="btn btn-theme-1 btn-sm"
                                :disabled="creating || !form.name || form.name.trim().length < 2">
                            <svg-icon :icon="creating ? 'sync' : 'plus'" :class="creating ? 'refresh' : ''"/>
                            Criar Chave
                        </button>
                    </div>
                </div>
            </div>
        </form>

        <Loading :visible="loading"/>

        <!-- Desktop -->
        <table v-if="$appUtils.isDesktop()"
               class="table table-hover table-striped table-sm table-bordered">
            <thead class="thead-dark thead-center">
            <tr>
                <th>NOME</th>
                <th>CHAVE</th>
                <th>CRIADA EM</th>
                <th>ÚLTIMO USO</th>
                <th>EXPIRA EM</th>
                <th>SITUAÇÃO</th>
                <th class="column-1"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="k in keys" :key="k.id" :class="{'row-inactive': k.status !== 'active'}">
                <td class="v-m">{{ k.name }}</td>
                <td class="v-m"><code>{{ k.masked }}</code></td>
                <td class="v-m t-c">{{ k.created_at | formatDate('DD/MM/YYYY HH:mm') }}</td>
                <td class="v-m t-c">
                    {{ k.last_used_at ? $options.filters.formatDate(k.last_used_at, 'DD/MM/YYYY HH:mm') : 'Nunca' }}
                </td>
                <td class="v-m t-c">
                    {{ k.expires_at ? $options.filters.formatDate(k.expires_at, 'DD/MM/YYYY') : '—' }}
                </td>
                <td class="v-m t-c">
                    <span class="badge" :class="badgeClass(k.status)">{{ statusLabel(k.status) }}</span>
                </td>
                <td class="t-c v-m">
                    <button v-if="k.status === 'active'" type="button" class="btn btn-danger btn-xs"
                            title="Revogar" @click="confirmRevoke(k)">
                        <svg-icon icon="ban"/>
                    </button>
                </td>
            </tr>
            <tr v-if="!loading && keys.length === 0">
                <td colspan="7" class="t-c empty">Você ainda não tem chaves de API.</td>
            </tr>
            </tbody>
        </table>

        <!-- Mobile -->
        <div v-else>
            <div class="list-card" v-for="k in keys" :key="k.id"
                 :class="{'row-inactive': k.status !== 'active'}">
                <span class="header">{{ k.name }}</span>
                <span class="stat">
                    <span class="badge" :class="badgeClass(k.status)">{{ statusLabel(k.status) }}</span>
                </span>
                <span class="key"><code>{{ k.masked }}</code></span>
                <span class="used">
                    Último uso:
                    {{ k.last_used_at ? $options.filters.formatDate(k.last_used_at, 'DD/MM/YYYY HH:mm') : 'Nunca' }}
                </span>
                <div class="act">
                    <button v-if="k.status === 'active'" type="button" class="btn btn-danger btn-sm"
                            @click="confirmRevoke(k)">
                        <svg-icon icon="ban"/> Revogar
                    </button>
                </div>
            </div>
            <div v-if="!loading && keys.length === 0" class="t-c empty">
                Você ainda não tem chaves de API.
            </div>
        </div>

        <!-- A chave recem-criada. Aparece UMA vez: o banco guarda so o HMAC, entao
             nao ha como reexibi-la depois. -->
        <modal v-if="created" id="modal-api-key-created" size="medium" :index="1">
            <template v-slot:header>Chave criada — copie agora</template>

            <template v-slot:body>
                <div class="alert alert-warning">
                    <strong>Esta é a única vez que a chave será exibida.</strong>
                    Guarde-a agora: o sistema armazena apenas um resumo criptográfico e não consegue
                    mostrá-la de novo. Se perdê-la, revogue e crie outra.
                </div>

                <div class="form-group">
                    <label>Sua chave</label>
                    <div class="input-group input-group-sm">
                        <input type="text" class="form-control" readonly :value="created.plaintext"
                               ref="plaintextInput" @focus="$event.target.select()"/>
                        <div class="input-group-append">
                            <button class="btn btn-dark" type="button" @click="copy(created.plaintext)">
                                <svg-icon icon="copy"/> Copiar
                            </button>
                        </div>
                    </div>
                </div>

                <div class="form-group">
                    <label>Para conectar no Claude Code</label>
                    <div class="input-group input-group-sm">
                        <textarea class="form-control cmd" rows="3" readonly :value="claudeCommand"
                                  @focus="$event.target.select()"></textarea>
                        <div class="input-group-append">
                            <button class="btn btn-dark" type="button" @click="copy(claudeCommand)">
                                <svg-icon icon="copy"/> Copiar
                            </button>
                        </div>
                    </div>

                    <!-- Em dev o comando aponta para o broker local, que precisa estar de pé.
                         O erro sem este aviso é confuso: o Claude Code diz apenas
                         "MCP endpoint not found". -->
                    <small v-if="isDev" class="dev-hint">
                        <svg-icon icon="info-circle"/>
                        Ambiente de desenvolvimento: o comando aponta para <code>{{ mcpUrl }}</code>.
                        O servidor MCP precisa estar rodando (<code>yarn dev</code> no projeto
                        <code>mcp.agrisoy.com.br</code>). A porta 3000 é do Rails e não responde MCP.
                    </small>
                </div>
            </template>

            <template v-slot:footer>
                <button type="button" class="btn btn-sm btn-primary" @click="dismissCreated">
                    <svg-icon icon="check"/> Já copiei
                </button>
            </template>
        </modal>
    </div>
</template>

<script>
    // Consentimento do assistente de IA (docs/mcp/16b-tempo-real.md). Os flags são
    // pessoais e vivem no localStorage deste navegador (StorageUtils); o heartbeat de
    // tela no router.afterEach e o AssistantMonitor os leem. Esta é a UI que a Fase 16b
    // deixou pendente — o texto da tool screen_context_get do MCP aponta para esta aba.
    //
    // As Chaves de API tambem vivem aqui: recurso de auto-servico ligado ao assistente,
    // migrado da antiga tela /settings/api-keys para o proprio perfil do usuario.
    import StorageUtils from "../../utils/StorageUtils";
    import Loading from '@/components/particles/Loading'

    /**
     * Endereço do servidor MCP, por ambiente.
     *
     * Em produção o nginx roteia `^~ /mcp` para o broker em 127.0.0.1:4100, então o
     * cliente usa o domínio normal. Em desenvolvimento não há nginx: o broker responde
     * direto na 4100, e a 3000 é o Rails — que não tem rota /mcp e devolve 404.
     *
     * O fallback fica no código, e não só no .env, porque os .env deste frontend não são
     * versionados: quem clonar o repo não os tem, e a tela mostraria um comando quebrado.
     * A env var continua valendo como override (ex.: broker em outra porta).
     */
    const MCP_URL =
        process.env.VUE_APP_MCP_URL ||
        (process.env.NODE_ENV === 'production'
            ? 'https://gestao.agrisoy.com.br/mcp'
            : 'http://127.0.0.1:4100/mcp');

    const IS_DEV = process.env.NODE_ENV !== 'production';

    const STATUS_LABELS = {
        active: 'Ativa',
        revoked: 'Revogada',
        expired: 'Expirada'
    };

    export default {
        name: "AssistantTab",
        components: {Loading},
        data() {
            return {
                sharesScreen: StorageUtils.assistantSharesScreen(),
                canNavigate: StorageUtils.assistantCanNavigate(),
                keys: [],
                loading: false,
                creating: false,
                created: null,
                isDev: IS_DEV,
                mcpUrl: MCP_URL,
                form: {
                    name: '',
                    expires_in_days: '90'
                }
            }
        },
        computed: {
            claudeCommand() {
                if (!this.created) return '';

                return `claude mcp add --transport http agrisoy ${MCP_URL} \\\n  --header "Authorization: Bearer ${this.created.plaintext}"`;
            }
        },
        mounted() {
            this.load();
        },
        methods: {
            onShareChange() {
                StorageUtils.setAssistantSharesScreen(this.sharesScreen);
                this.$snotify.simple(
                    this.sharesScreen
                        ? 'Ativado. Navegue para qualquer tela para o assistente começar a enxergar.'
                        : 'Desativado. O assistente não vê mais qual tela você acessa.',
                    'Assistente de IA',
                );
            },
            onNavigateChange() {
                StorageUtils.setAssistantCanNavigate(this.canNavigate);
                this.$snotify.simple(
                    this.canNavigate
                        ? 'Ativado. O assistente pode abrir telas no seu navegador.'
                        : 'Desativado. O assistente apenas sugere telas (sem abrir sozinho).',
                    'Assistente de IA',
                );
            },

            load() {
                this.loading = true;

                this.$appHttp.get('/v1/api_keys')
                    .then(({data}) => {
                        this.keys = data;
                    })
                    .catch(() => {
                        this.$snotify.error(this.$t('common.generalHttpError'), this.$appStorage.getUser().name);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },

            create() {
                if (this.creating) return;

                this.creating = true;

                this.$appHttp.post('/v1/api_keys', {
                    api_key: {
                        name: this.form.name.trim(),
                        expires_in_days: this.form.expires_in_days
                    }
                })
                    .then(({data}) => {
                        // Guardado so em memoria, ate o usuario fechar o modal.
                        this.created = data;
                        this.form.name = '';
                        this.load();
                    })
                    .catch(() => {
                        this.$snotify.error(this.$t('common.generalHttpError'), this.$appStorage.getUser().name);
                    })
                    .finally(() => {
                        this.creating = false;
                    });
            },

            confirmRevoke(key) {
                this.$store.commit('addModal', {
                    title: 'Revogar chave',
                    size: 'small',
                    body: `A chave "${key.name}" deixará de funcionar imediatamente e não pode ser reativada. Deseja revogar?`,
                    buttons: [
                        {
                            type: 'ok',
                            label: 'Revogar',
                            class: 'btn btn-danger btn-sm',
                            method: id => {
                                this.$store.commit('destroyModal', id);
                                this.revoke(key);
                            }
                        },
                        {
                            type: 'close',
                            label: 'Cancelar',
                            focus: true,
                            method: id => this.$store.commit('destroyModal', id)
                        }
                    ]
                });
            },

            revoke(key) {
                this.$appHttp.delete(`/v1/api_keys/${key.id}`)
                    .then(() => {
                        this.$snotify.success('Chave revogada com sucesso', this.$appStorage.getUser().name);
                        this.load();
                    })
                    .catch(() => {
                        this.$snotify.error(this.$t('common.generalHttpError'), this.$appStorage.getUser().name);
                    });
            },

            copy(text) {
                // A tela so roda sob HTTPS em producao, mas o clipboard falha em contexto
                // inseguro (http://localhost) — o fallback evita botao morto em dev.
                if (navigator.clipboard && window.isSecureContext) {
                    navigator.clipboard.writeText(text)
                        .then(() => this.$snotify.success('Copiado', this.$appStorage.getUser().name))
                        .catch(() => this.selectFallback());
                    return;
                }

                this.selectFallback();
            },

            selectFallback() {
                if (this.$refs.plaintextInput) this.$refs.plaintextInput.select();
                this.$snotify.warning('Não foi possível copiar automaticamente. Selecione e copie com Ctrl+C.',
                    this.$appStorage.getUser().name);
            },

            dismissCreated() {
                this.created = null;
            },

            statusLabel(status) {
                return STATUS_LABELS[status] || status;
            },

            badgeClass(status) {
                if (status === 'active') return 'badge-success';
                if (status === 'revoked') return 'badge-danger';

                return 'badge-secondary';
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "../../assets/css/variables";

    .assistant-intro {
        color: #666;
        font-size: 13px;
        margin-bottom: 15px;
    }

    .section-divider {
        margin: 20px 0 15px;
        border: 0;
        border-top: 1px solid #e7eaec;
    }

    .section-title {
        margin-bottom: 8px;
    }

    .intro {
        font-size: .85em;
        color: #676a6c;
        margin-bottom: 15px;
    }

    .empty {
        padding: 20px;
        color: #999;
    }

    .row-inactive {
        opacity: .55;
    }

    code {
        font-size: .85em;
        color: $theme_color_one;
    }

    .cmd {
        font-family: monospace;
        font-size: .78em;
        white-space: pre;
    }

    .dev-hint {
        display: block;
        margin-top: 8px;
        color: #8a6d3b;
        background: #fcf8e3;
        border: 1px solid #faebcc;
        border-radius: 3px;
        padding: 6px 8px;
        font-size: .78em;
        line-height: 1.5;

        code {
            color: #8a6d3b;
            font-weight: 600;
        }
    }

    .list-card {
        display: grid;
        width: 100%;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto;
        grid-template-areas: "header stat"
            "key key"
            "used used"
            "act act";
        grid-gap: 5px;
        font-size: .8em;
        padding: 5px;

        &:not(:first-child) {
            border-top: 1px solid #e7eaec;
        }

        .header {
            color: $theme_color_one;
            font-weight: 700;
            grid-area: header;
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
        }

        .stat {
            grid-area: stat;
            text-align: right;
        }

        .key {
            grid-area: key;
            word-break: break-all;
        }

        .used {
            grid-area: used;
        }

        .act {
            grid-area: act;
            text-align: right;
        }
    }
</style>
